import {
    Box,
    Button,
    Divider,
    Grid2 as Grid,
    Slider,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const MANUAL_MODE = 'manual';
const ESTIMATED_MODE = 'estimated';
const VT1_PERCENTAGES = { lower: 0.4, estimate: 0.5, upper: 0.6 };
const VT2_PERCENTAGES = { lower: 0.75, estimate: 0.8, upper: 0.85 };

const toPositiveNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : null;
};

export const HeartRateConfiguration = ({ formik }) => {
    const { t } = useTranslation();
    const { values, errors, touched, handleBlur, handleChange, setFieldValue } = formik;
    const [mode, setMode] = useState(MANUAL_MODE);
    const [intensityRange, setIntensityRange] = useState([60, 70]);
    const [maxWasEstimated, setMaxWasEstimated] = useState(false);
    const [vt1WasEstimated, setVt1WasEstimated] = useState(false);
    const [vt2WasEstimated, setVt2WasEstimated] = useState(false);
    const manualTrainingValues = useRef({
        minimum: values.minTrainingHeartRate,
        maximum: values.trainingHeartRate
    });

    const age = useMemo(() => {
        if (!values.birthdate || !dayjs(values.birthdate).isValid()) {
            return null;
        }

        const calculatedAge = dayjs().diff(dayjs(values.birthdate), 'year');
        return calculatedAge >= 0 ? calculatedAge : null;
    }, [values.birthdate]);

    const estimatedMaxHeartRate = age !== null ? 220 - age : null;
    const maxHeartRate = toPositiveNumber(values.maxHeartRate);
    const restingHeartRate = toPositiveNumber(values.restingHeartRate);
    const canEstimate =
        maxHeartRate !== null &&
        restingHeartRate !== null &&
        maxHeartRate > restingHeartRate;

    const estimatedTrainingRange = useMemo(() => {
        if (!canEstimate) return null;

        const reserve = maxHeartRate - restingHeartRate;
        return {
            minimum: Math.round(reserve * (intensityRange[0] / 100) + restingHeartRate),
            maximum: Math.round(reserve * (intensityRange[1] / 100) + restingHeartRate)
        };
    }, [canEstimate, intensityRange, maxHeartRate, restingHeartRate]);

    const estimatedVentilatoryThresholds = useMemo(() => {
        if (!canEstimate) return null;

        const reserve = maxHeartRate - restingHeartRate;
        const calculateThreshold = (percentage) =>
            Math.round(restingHeartRate + reserve * percentage);

        return {
            vt1: {
                lower: calculateThreshold(VT1_PERCENTAGES.lower),
                estimate: calculateThreshold(VT1_PERCENTAGES.estimate),
                upper: calculateThreshold(VT1_PERCENTAGES.upper)
            },
            vt2: {
                lower: calculateThreshold(VT2_PERCENTAGES.lower),
                estimate: calculateThreshold(VT2_PERCENTAGES.estimate),
                upper: calculateThreshold(VT2_PERCENTAGES.upper)
            }
        };
    }, [canEstimate, maxHeartRate, restingHeartRate]);

    useEffect(() => {
        if (mode !== MANUAL_MODE) return;

        manualTrainingValues.current = {
            minimum: values.minTrainingHeartRate,
            maximum: values.trainingHeartRate
        };
    }, [values.minTrainingHeartRate, values.trainingHeartRate, mode]);

    useEffect(() => {
        if (
            mode !== ESTIMATED_MODE ||
            !maxWasEstimated ||
            estimatedMaxHeartRate === null ||
            values.maxHeartRate === estimatedMaxHeartRate
        ) {
            return;
        }

        setFieldValue('maxHeartRate', estimatedMaxHeartRate, false);
    }, [
        estimatedMaxHeartRate,
        maxWasEstimated,
        mode,
        setFieldValue,
        values.maxHeartRate
    ]);

    useEffect(() => {
        if (mode !== ESTIMATED_MODE) return;

        const minimum = estimatedTrainingRange?.minimum ?? '';
        const maximum = estimatedTrainingRange?.maximum ?? '';

        if (values.minTrainingHeartRate !== minimum) {
            setFieldValue('minTrainingHeartRate', minimum, false);
        }
        if (values.trainingHeartRate !== maximum) {
            setFieldValue('trainingHeartRate', maximum, false);
        }
    }, [
        estimatedTrainingRange,
        mode,
        setFieldValue,
        values.minTrainingHeartRate,
        values.trainingHeartRate
    ]);

    useEffect(() => {
        if (!vt1WasEstimated) return;

        const estimate = estimatedVentilatoryThresholds?.vt1.estimate ?? '';
        if (values.anaerobicThreshold !== estimate) {
            setFieldValue('anaerobicThreshold', estimate, false);
        }
    }, [
        estimatedVentilatoryThresholds,
        setFieldValue,
        values.anaerobicThreshold,
        vt1WasEstimated
    ]);

    useEffect(() => {
        if (!vt2WasEstimated) return;

        const estimate = estimatedVentilatoryThresholds?.vt2.estimate ?? '';
        if (values.ventilatoryThreshold !== estimate) {
            setFieldValue('ventilatoryThreshold', estimate, false);
        }
    }, [
        estimatedVentilatoryThresholds,
        setFieldValue,
        values.ventilatoryThreshold,
        vt2WasEstimated
    ]);

    const getFieldProps = (name) => ({
        name,
        value: values[name],
        onBlur: handleBlur,
        error: Boolean(touched[name] && errors[name]),
        helperText: touched[name] && errors[name]
    });

    const handleMaximumChange = (event) => {
        setMaxWasEstimated(false);
        handleChange(event);
    };

    const applyMaximumEstimate = () => {
        if (estimatedMaxHeartRate === null) return;

        setMaxWasEstimated(true);
        setFieldValue('maxHeartRate', estimatedMaxHeartRate);
    };

    const handleVt1Change = (event) => {
        setVt1WasEstimated(false);
        handleChange(event);
    };

    const handleVt2Change = (event) => {
        setVt2WasEstimated(false);
        handleChange(event);
    };

    const applyVt1Estimate = () => {
        if (!estimatedVentilatoryThresholds) return;

        setVt1WasEstimated(true);
        setFieldValue('anaerobicThreshold', estimatedVentilatoryThresholds.vt1.estimate);
    };

    const applyVt2Estimate = () => {
        if (!estimatedVentilatoryThresholds) return;

        setVt2WasEstimated(true);
        setFieldValue(
            'ventilatoryThreshold',
            estimatedVentilatoryThresholds.vt2.estimate
        );
    };

    const handleModeChange = (_event, nextMode) => {
        if (!nextMode || nextMode === mode) return;

        if (nextMode === ESTIMATED_MODE) {
            manualTrainingValues.current = {
                minimum: values.minTrainingHeartRate,
                maximum: values.trainingHeartRate
            };

            if (!maxHeartRate && estimatedMaxHeartRate !== null) {
                setMaxWasEstimated(true);
                setFieldValue('maxHeartRate', estimatedMaxHeartRate, false);
            }
        } else {
            setFieldValue(
                'minTrainingHeartRate',
                manualTrainingValues.current.minimum,
                false
            );
            setFieldValue(
                'trainingHeartRate',
                manualTrainingValues.current.maximum,
                false
            );
        }

        setMode(nextMode);
    };

    const missingReferenceMessage = !restingHeartRate
        ? t('calculator.restingRequired')
        : !maxHeartRate
          ? t('calculator.maximumRequired')
          : t('calculator.maximumMustExceedResting');

    return (
        <Grid container spacing={2.5}>
            <Grid size={12}>
                <Typography variant="subtitle1" fontWeight={700}>
                    {t('calculator.referenceValues')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t('calculator.referenceValuesDescription')}
                </Typography>
            </Grid>

            <Grid size={12}>
                <TextField
                    {...getFieldProps('restingHeartRate')}
                    fullWidth
                    type="number"
                    label={t('users.fields.restingHR')}
                    onChange={handleChange}
                    slotProps={{ htmlInput: { min: 1 } }}
                />
            </Grid>
            <Grid size={12}>
                <TextField
                    {...getFieldProps('maxHeartRate')}
                    fullWidth
                    type="number"
                    label={t('users.fields.maxHR')}
                    onChange={handleMaximumChange}
                    slotProps={{ htmlInput: { min: 1 } }}
                />
                {estimatedMaxHeartRate !== null && (
                    <Box
                        sx={{
                            mt: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">
                            {t('calculator.ageEstimate', {
                                value: estimatedMaxHeartRate
                            })}
                        </Typography>
                        <Button
                            size="small"
                            onClick={applyMaximumEstimate}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            {t('calculator.useEstimate')}
                        </Button>
                    </Box>
                )}
            </Grid>
            <Grid size={12}>
                <TextField
                    {...getFieldProps('anaerobicThreshold')}
                    fullWidth
                    type="number"
                    label={t('users.fields.vt1HR')}
                    onChange={handleVt1Change}
                    slotProps={{ htmlInput: { min: 1 } }}
                />
                <Box
                    sx={{
                        mt: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1
                    }}
                >
                    <Typography variant="caption" color="text.secondary">
                        {estimatedVentilatoryThresholds
                            ? t('calculator.thresholdEstimate', {
                                  value: estimatedVentilatoryThresholds.vt1.estimate,
                                  lower: estimatedVentilatoryThresholds.vt1.lower,
                                  upper: estimatedVentilatoryThresholds.vt1.upper
                              })
                            : t('calculator.thresholdReferencesRequired')}
                    </Typography>
                    <Button
                        size="small"
                        disabled={!estimatedVentilatoryThresholds}
                        onClick={applyVt1Estimate}
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        {t('calculator.useEstimate')}
                    </Button>
                </Box>
            </Grid>
            <Grid size={12}>
                <TextField
                    {...getFieldProps('ventilatoryThreshold')}
                    fullWidth
                    type="number"
                    label={t('users.fields.vt2HR')}
                    onChange={handleVt2Change}
                    slotProps={{ htmlInput: { min: 1 } }}
                />
                <Box
                    sx={{
                        mt: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1
                    }}
                >
                    <Typography variant="caption" color="text.secondary">
                        {estimatedVentilatoryThresholds
                            ? t('calculator.thresholdEstimate', {
                                  value: estimatedVentilatoryThresholds.vt2.estimate,
                                  lower: estimatedVentilatoryThresholds.vt2.lower,
                                  upper: estimatedVentilatoryThresholds.vt2.upper
                              })
                            : t('calculator.thresholdReferencesRequired')}
                    </Typography>
                    <Button
                        size="small"
                        disabled={!estimatedVentilatoryThresholds}
                        onClick={applyVt2Estimate}
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        {t('calculator.useEstimate')}
                    </Button>
                </Box>
            </Grid>

            <Grid size={12}>
                <Divider />
            </Grid>

            <Grid size={12}>
                <Typography variant="subtitle1" fontWeight={700}>
                    {t('calculator.trainingZone')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {t('calculator.trainingZoneDescription')}
                </Typography>
                <ToggleButtonGroup
                    exclusive
                    fullWidth
                    size="small"
                    value={mode}
                    onChange={handleModeChange}
                    aria-label={t('calculator.trainingZone')}
                    sx={{
                        gap: 1.5,
                        '& .MuiToggleButton-root': {
                            flex: 1,
                            textTransform: 'none',
                            fontWeight: 700,
                            py: 0.75,
                            color: '#0A579B',
                            border: '1px solid #0A579B !important',
                            borderRadius: '6px !important',
                            margin: '0 !important'
                        },
                        '& .MuiToggleButton-root.Mui-selected': {
                            bgcolor: '#0A579B',
                            color: '#fff'
                        },
                        '& .MuiToggleButton-root.Mui-selected:hover': {
                            bgcolor: '#084472'
                        }
                    }}
                >
                    <ToggleButton value={MANUAL_MODE}>
                        {t('calculator.manualMode')}
                    </ToggleButton>
                    <ToggleButton value={ESTIMATED_MODE}>
                        {t('calculator.estimatedMode')}
                    </ToggleButton>
                </ToggleButtonGroup>
            </Grid>

            {mode === ESTIMATED_MODE && (
                <Grid size={12}>
                    <Box sx={{ px: 1 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 0.5
                            }}
                        >
                            <Typography variant="body2" fontWeight={700}>
                                {t('calculator.intensity')}
                            </Typography>
                            <Typography variant="body2" color="primary.main">
                                {intensityRange[0]}% - {intensityRange[1]}%
                            </Typography>
                        </Box>
                        <Slider
                            value={intensityRange}
                            onChange={(_event, value) => setIntensityRange(value)}
                            step={5}
                            min={40}
                            max={100}
                            valueLabelDisplay="auto"
                            disableSwap
                        />
                        <Typography variant="caption" color="text.secondary">
                            {canEstimate
                                ? t('calculator.referencesUsed', {
                                      resting: restingHeartRate,
                                      maximum: maxHeartRate
                                  })
                                : missingReferenceMessage}
                        </Typography>
                    </Box>
                </Grid>
            )}

            <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                    {...getFieldProps('minTrainingHeartRate')}
                    fullWidth
                    type="number"
                    label={t('calculator.minimumTrainingLimit')}
                    onChange={handleChange}
                    slotProps={{
                        input: { readOnly: mode === ESTIMATED_MODE },
                        htmlInput: { min: 1 }
                    }}
                />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                    {...getFieldProps('trainingHeartRate')}
                    fullWidth
                    type="number"
                    label={t('calculator.maximumTrainingLimit')}
                    onChange={handleChange}
                    slotProps={{
                        input: { readOnly: mode === ESTIMATED_MODE },
                        htmlInput: { min: 1 }
                    }}
                />
            </Grid>

            {mode === ESTIMATED_MODE && estimatedTrainingRange && (
                <Grid size={12}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            px: 2,
                            py: 1.5,
                            bgcolor: '#EEF6FD',
                            borderLeft: '4px solid',
                            borderColor: 'primary.main'
                        }}
                    >
                        <Box>
                            <Typography variant="body2" fontWeight={700}>
                                {t('calculator.estimatedTrainingZone')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {t('calculator.karvonenMethod')}
                            </Typography>
                        </Box>
                        <Typography
                            variant="h4"
                            color="primary.main"
                            fontWeight={700}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            {estimatedTrainingRange.minimum} -{' '}
                            {estimatedTrainingRange.maximum} bpm
                        </Typography>
                    </Box>
                </Grid>
            )}
        </Grid>
    );
};

HeartRateConfiguration.propTypes = {
    formik: PropTypes.shape({
        values: PropTypes.object.isRequired,
        errors: PropTypes.object.isRequired,
        touched: PropTypes.object.isRequired,
        handleBlur: PropTypes.func.isRequired,
        handleChange: PropTypes.func.isRequired,
        setFieldValue: PropTypes.func.isRequired
    }).isRequired
};
